import React from 'react';
import { Phone, Mail, MapPin, Shield, Users, Globe, CheckCircle, Star, Heart, Award, Clock, ArrowLeft, Facebook, Instagram, Twitter, Linkedin, Youtube } from 'lucide-react';

const Header = () => {
  return (
    <header className="fixed top-0 left-0 w-full bg-white shadow-sm z-50">
      <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <a href="/" className="flex items-center gap-1 text-gray-600 hover:text-purple-600 text-sm font-medium">
            <ArrowLeft className="w-4 h-4" /> Back
          </a>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center text-white font-bold">
              K
            </div>
            <span className="font-bold text-lg text-gray-800">KoPartner</span>
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-700">
          <a href="/services" className="hover:text-purple-600">Services</a>
          <a href="/why-choose-us" className="hover:text-purple-600">Why Choose Us</a>
          <a href="/pricing" className="hover:text-purple-600">Pricing</a>
          <a href="/login" className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition">
            Login
          </a>
        </nav>
      </div>
    </header>
  );
};

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-gray-300 py-12">
      <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-4 gap-8">
        <div>
          <h4 className="text-white font-bold mb-4">Company</h4>
          <ul className="space-y-2 text-sm">
            <li><a href="/about" className="hover:text-purple-400">About</a></li>
            <li><a href="/faq" className="hover:text-purple-400">FAQ</a></li>
            <li><a href="/contact" className="hover:text-purple-400">Contact</a></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-bold mb-4">Legal</h4>
          <ul className="space-y-2 text-sm">
            <li><a href="/privacy-policy" className="hover:text-purple-400">Privacy Policy</a></li>
            <li><a href="/terms" className="hover:text-purple-400">Terms</a></li>
            <li><a href="/refund-policy" className="hover:text-purple-400">Refund Policy</a></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-bold mb-4">Support</h4>
          <ul className="space-y-2 text-sm">
            <li><a href="/help-center" className="hover:text-purple-400">Help Center</a></li>
            <li><a href="/code-of-conduct" className="hover:text-purple-400">Code of Conduct</a></li>
            <li><a href="/admin-login" className="hover:text-purple-400">Admin Login</a></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-bold mb-4">Connect With Us</h4>
          <div className="flex gap-4">
            <a href="#" className="hover:text-purple-400"><Facebook className="w-5 h-5" /></a>
            <a href="#" className="hover:text-purple-400"><Instagram className="w-5 h-5" /></a>
            <a href="#" className="hover:text-purple-400"><Twitter className="w-5 h-5" /></a>
            <a href="#" className="hover:text-purple-400"><Linkedin className="w-5 h-5" /></a>
            <a href="#" className="hover:text-purple-400"><Youtube className="w-5 h-5" /></a>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 mt-10 pt-6 border-t border-gray-800 text-center text-sm text-gray-400">
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="w-6 h-6 rounded bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center text-white font-bold text-xs">
            K
          </div>
          <span className="font-semibold text-gray-300">KoPartner</span>
        </div>
        <p>© 2026 KoPartner (A unit of SET INDIA BUSINESS PVT LTD). All rights reserved.</p>
        <p>Made with ❤️ in India</p>
      </div>
    </footer>
  );
};

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <div className="max-w-4xl mx-auto px-4 pt-32 pb-12 text-center">
        <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
          🏆 India's #1 Professional Social Support Platform
        </div>

        <h1 className="text-5xl md:text-6xl font-extrabold mb-8 text-purple-700">
          About KoPartner
        </h1>

        <p className="text-gray-500 text-lg leading-relaxed max-w-3xl mx-auto">
          "People are extremely active on social media, but disconnected from relatives, neighbours, and even close friends. When they need help -- medical assistance, someone to shop with, or even just a movie partner -- they often have no one to turn to."
        </p>
        <p className="text-gray-700 font-semibold mt-6">
          This isn't just a quote. This is the reality of modern India.
        </p>
      </div>

      {/* The Reality We Address - Full width gradient */}
      <section className="bg-gradient-to-br from-purple-600 via-purple-600 to-pink-600 py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-10">
            The Reality We Address
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-left border border-white/20">
              <p className="text-white/90 leading-relaxed">
                A nation of <strong className="text-white">1.4 billion people</strong> where loneliness is becoming an epidemic. Where a senior citizen needs someone to accompany them to a hospital but has no one available.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-left border border-white/20">
              <p className="text-white/90 leading-relaxed">
                Where a young professional moves to a new city and doesn't know a single person. Where a single parent needs help but doesn't want to burden anyone.
              </p>
            </div>
          </div>

          <p className="text-white text-xl md:text-2xl font-bold">
            That's why KoPartner exists. Not as a luxury. As a necessity.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-12 space-y-8">
        {/* Stats - 4 Cards */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-purple-50 p-4 rounded-xl text-center">
            <p className="text-2xl font-bold text-purple-600">Millions</p>
            <p className="text-sm text-gray-600">Registered KoPartners</p>
          </div>
          <div className="bg-pink-50 p-4 rounded-xl text-center">
            <p className="text-2xl font-bold text-pink-600">All India</p>
            <p className="text-sm text-gray-600">Cities &amp; Pin Codes</p>
          </div>
          <div className="bg-indigo-50 p-4 rounded-xl text-center">
            <p className="text-2xl font-bold text-indigo-600">AI-Powered</p>
            <p className="text-sm text-gray-600">Verified Profiles</p>
          </div>
          <div className="bg-green-50 p-4 rounded-xl text-center">
            <p className="text-2xl font-bold text-green-600">100%</p>
            <p className="text-sm text-gray-600">Secure Payments</p>
          </div>
        </section>

        {/* What We Do */}
        <section className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold mb-4 text-purple-800">What We Do</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            <strong>KoPartner is India's trusted social and lifestyle support platform.</strong> We connect individuals who need companionship and assistance with verified, professional KoPartners who provide safe, consent-first, strictly professional services.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
            <div className="flex items-center gap-2 bg-purple-50 p-3 rounded-xl">
              <span className="text-xl">👴</span>
              <span className="text-sm font-medium text-purple-700">Elder Care &amp; Senior Assistance</span>
            </div>
            <div className="flex items-center gap-2 bg-pink-50 p-3 rounded-xl">
              <span className="text-xl">🛍️</span>
              <span className="text-sm font-medium text-pink-700">Shopping &amp; Errand Assistance</span>
            </div>
            <div className="flex items-center gap-2 bg-indigo-50 p-3 rounded-xl">
              <span className="text-xl">🎉</span>
              <span className="text-sm font-medium text-indigo-700">Social Events &amp; Clubbing Companions</span>
            </div>
            <div className="flex items-center gap-2 bg-violet-50 p-3 rounded-xl">
              <span className="text-xl">🩺</span>
              <span className="text-sm font-medium text-violet-700">Medical Support &amp; Hospital Visits</span>
            </div>
            <div className="flex items-center gap-2 bg-fuchsia-50 p-3 rounded-xl">
              <span className="text-xl">🎬</span>
              <span className="text-sm font-medium text-fuchsia-700">Movie &amp; Entertainment Partners</span>
            </div>
            <div className="flex items-center gap-2 bg-rose-50 p-3 rounded-xl">
              <span className="text-xl">✈️</span>
              <span className="text-sm font-medium text-rose-700">Travel Partners &amp; Companions</span>
            </div>
          </div>
        </section>

        {/* Our Mission */}
        <section className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl shadow-lg p-8 border border-purple-100">
          <h2 className="text-2xl font-bold mb-4 text-purple-800">Our Mission</h2>
          <p className="text-gray-700 leading-relaxed">
            To bridge the social support gap in India by providing <strong>accessible, affordable, and professional companionship services</strong> to every individual across all cities and pin codes. We aim to combat loneliness, provide dignified assistance to seniors, and create meaningful earning opportunities for millions of Indians.
          </p>
        </section>

        {/* Our Vision */}
        <section className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl shadow-lg p-8 border border-pink-100">
          <h2 className="text-2xl font-bold mb-4 text-pink-800">Our Vision</h2>
          <p className="text-gray-700 leading-relaxed">
            To become the <strong>most trusted name in professional social support services</strong> in India and beyond. We envision a world where no one feels alone, where help is always just a click away, and where providing care is a respected profession that empowers millions to achieve financial independence.
          </p>
        </section>

        {/* Our Core Values */}
        <section className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold mb-6 text-center text-purple-800">Our Core Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-purple-50 p-5 rounded-xl border border-purple-100">
              <div className="flex items-center gap-3 mb-2">
                <Shield className="w-6 h-6 text-purple-600" />
                <h3 className="text-lg font-bold text-purple-800">Safety First</h3>
              </div>
              <p className="text-gray-600 text-sm">AI-powered verification, SOS button, and secure payouts</p>
            </div>
            <div className="bg-pink-50 p-5 rounded-xl border border-pink-100">
              <div className="flex items-center gap-3 mb-2">
                <Heart className="w-6 h-6 text-pink-600" />
                <h3 className="text-lg font-bold text-pink-800">Consent-First</h3>
              </div>
              <p className="text-gray-600 text-sm">Strictly professional, respectful interactions only</p>
            </div>
            <div className="bg-indigo-50 p-5 rounded-xl border border-indigo-100">
              <div className="flex items-center gap-3 mb-2">
                <Globe className="w-6 h-6 text-indigo-600" />
                <h3 className="text-lg font-bold text-indigo-800">All India Coverage</h3>
              </div>
              <p className="text-gray-600 text-sm">Available across all cities and pin codes in India</p>
            </div>
            <div className="bg-violet-50 p-5 rounded-xl border border-violet-100">
              <div className="flex items-center gap-3 mb-2">
                <Award className="w-6 h-6 text-violet-600" />
                <h3 className="text-lg font-bold text-violet-800">Professionalism</h3>
              </div>
              <p className="text-gray-600 text-sm">Trained partners with clear code of conduct</p>
            </div>
          </div>
        </section>

        {/* Why Choose KoPartner */}
        <section className="bg-gradient-to-r from-purple-600 via-purple-700 to-pink-600 rounded-2xl shadow-lg p-8 text-white">
          <h2 className="text-3xl font-bold mb-6 text-center">Why Choose KoPartner?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white/10 backdrop-blur-sm p-5 rounded-xl border border-white/20">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-6 h-6" />
                <h3 className="text-lg font-bold">Millions of Verified KoPartners</h3>
              </div>
              <p className="text-sm text-purple-100">The largest network of verified professional companions across India</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-5 rounded-xl border border-white/20">
              <div className="flex items-center gap-3 mb-2">
                <MapPin className="w-6 h-6" />
                <h3 className="text-lg font-bold">All India Coverage</h3>
              </div>
              <p className="text-sm text-purple-100">Available in every city and pin code across India - truly nationwide</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-5 rounded-xl border border-white/20 md:col-span-2">
              <div className="flex items-center gap-3 mb-2">
                <Shield className="w-6 h-6" />
                <h3 className="text-lg font-bold">Safe &amp; Secure</h3>
              </div>
              <p className="text-sm text-purple-100">SOS button, verified profiles, and secure payouts directly to bank accounts</p>
            </div>
          </div>
          <div className="text-center mt-6 pt-6 border-t border-white/20">
            <p className="text-lg font-semibold">Join India's #1 Social Support Platform</p>
            <p className="text-sm text-purple-100 mt-1">Whether you need support or want to earn by helping others - KoPartner is here for you.</p>
            <div className="flex flex-wrap gap-4 justify-center mt-4">
              <button className="px-6 py-2 bg-white text-purple-700 rounded-xl font-semibold hover:bg-purple-50 transition">
                Find a KoPartner
              </button>
              <button className="px-6 py-2 bg-purple-800/50 text-white rounded-xl font-semibold hover:bg-purple-800/70 transition border border-white/30">
                Become a KoPartner
              </button>
            </div>
          </div>
        </section>

      </div>

      <Footer />
    </div>
  );
};

export default AboutPage;
